import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Stat, StatGroup, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useResellerDashboard, type TopReseller } from '@/api/resellers';
import { resellerTierTone, humanize } from './reseller-tones';
import { StatusPill } from '@vnetwork/ui/data';
import { formatMoney } from '@/lib/format';

/** Stat cards (5-up) + top resellers table — mirrors legacy DashboardStats. */
export function ResellerDashboardSection() {
  const { data, isLoading, isError } = useResellerDashboard();
  const stats = data?.result;
  const navigate = useNavigate();

  if (isError) {
    return (
      <Alert tone="danger" title="Failed to load dashboard stats" />
    );
  }

  const topResellers: (TopReseller & { rank: number })[] = (stats?.top_resellers ?? []).map(
    (r, i) => ({ ...r, rank: i + 1 }),
  );

  const topColumns: ColumnDef<TopReseller & { rank: number }, unknown>[] = [
    { id: 'rank', header: '#', cell: ({ row }) => row.original.rank },
    {
      id: 'reseller_name',
      header: 'Reseller',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.reseller_name}
        </span>
      ),
    },
    {
      id: 'reseller_tier',
      header: 'Tier',
      cell: ({ row }) => (
        <StatusPill tone={resellerTierTone(row.original.reseller_tier)}>
          {humanize(row.original.reseller_tier)}
        </StatusPill>
      ),
    },
    {
      id: 'customer_count',
      header: () => <div style={{ textAlign: 'right' }}>Customers</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {row.original.customer_count}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Stat cards */}
      <Card>
        <CardBody>
          {isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--vn-space-4)' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  <Skeleton style={{ height: 16, width: 80 }} />
                  <Skeleton style={{ height: 28, width: 120 }} />
                </div>
              ))}
            </div>
          ) : (
            <StatGroup columns={5}>
              <Stat label="Total Resellers" value={String(stats?.total_resellers ?? 0)} />
              <Stat label="Total Customers" value={String(stats?.total_customers ?? 0)} />
              <Stat label="Active Services" value={String(stats?.total_active_services ?? 0)} />
              <Stat label="Monthly Recurring" value={formatMoney(stats?.total_mrr ?? 0)} />
              <Stat label="Total Revenue" value={formatMoney(stats?.total_revenue ?? 0)} />
            </StatGroup>
          )}
        </CardBody>
      </Card>

      {/* Top resellers table — only rendered when there is data */}
      {!isLoading && topResellers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Star size={16} style={{ color: 'var(--vn-warning-text)' }} />
              Top Resellers
            </CardTitle>
          </CardHeader>
          <CardBody style={{ padding: 0 }}>
            <DataTable
              columns={topColumns}
              data={topResellers}
              getRowId={(r) => String(r.reseller_id)}
              onRowClick={(r) => navigate(`/resellers/${r.reseller_id}`)}
              emptyState={<EmptyState title="No top resellers" />}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
}
